import { createFileRoute } from "@tanstack/react-router";
import { optionParam, textParam } from "@/utils/searchParams";
import {
  REQUEST_PHASES,
  type RequestPhase,
} from "@/components/requests/requestPhase";
import type { RequestPriority, RequestType } from "@/hooks/types";

// The queue's tab and filters live in the URL, so opening a request and coming
// back lands on the queue the admin was reading (NAV-45).
export const REQUEST_TABS = [
  "ACCESS",
  "EVALUATION",
] as const satisfies readonly RequestType[];
export const REQUEST_PRIORITIES = [
  "URGENT",
  "HIGH",
  "NORMAL",
  "LOW",
] as const satisfies readonly RequestPriority[];

/** The queue's order (Jira NAV-258). Decided requests come after every open
 *  one in either order. Unset: stalled first, then newest, so what is stuck
 *  stays on top of the default view. `wait`: whatever has been
 *  with its current owner longest first; a wait on the requester's answer is
 *  not the owner's and does not count. */
export const REQUEST_SORTS = ["wait"] as const;
export type RequestSort = (typeof REQUEST_SORTS)[number];

export type RequestTab = (typeof REQUEST_TABS)[number];
export type RequestPriorityFilter = (typeof REQUEST_PRIORITIES)[number];

export type BuRequestsSearch = {
  tab?: RequestTab;
  q?: string;
  /**
   * Every phase, not just every status: the dropdown offers "not started" and
   * "in review" on the evaluations tab, and a value `validateSearch` rejects is
   * dropped on the way back — so those two would be the one pair of filters
   * that did not survive the round trip this route exists for.
   *
   * `REQUEST_PHASES` is derived from a total map over `RequestPhase`, and the
   * statuses inside it are pinned to the backend enum by the requests test, so
   * neither half can quietly narrow.
   */
  status?: RequestPhase;
  priority?: RequestPriorityFilter;
  sort?: RequestSort;
  /** Only stalled requests (`requestStall`). */
  stalled?: boolean;
};

/** Whether "Stalled only" narrows the list. On both tabs, where its switch is
 *  drawn on both (Jira NAV-259): an access request stalls with its approver,
 *  its service desk or the admins as an evaluation does with its steps. */
export function stalledFilterActive(
  search: Pick<BuRequestsSearch, "tab" | "stalled">,
): boolean {
  return !!search.stalled;
}

export const Route = createFileRoute("/bu-admin/requests")({
  validateSearch: (search: Record<string, unknown>): BuRequestsSearch => ({
    tab: optionParam(REQUEST_TABS, search.tab),
    q: textParam(search.q),
    status: optionParam(REQUEST_PHASES, search.status),
    priority: optionParam(REQUEST_PRIORITIES, search.priority),
    sort: optionParam(REQUEST_SORTS, search.sort),
    stalled:
      search.stalled === true || search.stalled === "true" ? true : undefined,
  }),
});
